import React from 'react';
import { Link } from 'react-router-dom';
import { getImagePath } from '../utils/imagePath';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import SEOHead from '../components/SEOHead';
import CookieConsent from '../components/CookieConsent';

const AccessDenied = () => {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();

  // Si l'utilisateur est authentifié, rediriger vers la page d'accueil
  if (isAuthenticated) {
    return null; // Le ProtectedRoute gérera la redirection
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
      <SEOHead 
        title={t('accessDenied.title')}
        description={t('accessDenied.message')}
        keywords="accès refusé, authentification, Golden Mali"
        noindex={true}
      />
      {/* Effet de fond animé */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gold-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Logo */}
        <div className="mb-8">
          <img 
            src={getImagePath("/assets/logo/GM.png")} 
            alt="Golden Mali SARL" 
            className="h-24 w-24 mx-auto mb-6 rounded-xl bg-white p-3 shadow-2xl"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <h1 className="text-4xl md:text-5xl font-black text-white mb-2">
            {t('landing.title')}
          </h1>
          <p className="text-lg text-gold-300 font-semibold">
            {t('landing.subtitle')}
          </p>
        </div>

        {/* Carte principale */}
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 md:p-12 border-2 border-gold-400">
          {/* Icône de sécurité */}
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4">
              <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
          </div>

          {/* Titre */}
          <h2 className="text-3xl md:text-4xl font-black text-navy-800 mb-4">
            {t('accessDenied.title')}
          </h2>
          
          {/* Message */}
          <p className="text-lg md:text-xl text-gray-700 mb-6 leading-relaxed">
            {t('accessDenied.message')}
          </p>

          {/* Informations supplémentaires */}
          <div className="bg-navy-50 rounded-lg p-6 mb-8 border-l-4 border-gold-500">
            <p className="text-sm text-gray-600 mb-2">
              <span className="font-semibold text-navy-800">{t('landing.security.title')}:</span>
            </p>
            <ul className="text-sm text-gray-600 text-left space-y-2">
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-gold-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{t('landing.security.dataSecure.description')}</span>
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-gold-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{t('landing.security.accessControlled.description')}</span>
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-gold-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{t('landing.security.cloudSync.description')}</span>
              </li>
            </ul>
          </div>

          {/* Bouton de connexion */}
          <Link
            to="/login"
            className="inline-block w-full md:w-auto px-8 py-4 bg-gradient-to-r from-gold-500 to-gold-600 text-white text-lg font-black rounded-xl shadow-2xl hover:shadow-gold-500/50 hover:from-gold-600 hover:to-gold-700 transition-all duration-300 transform hover:scale-105 active:scale-95"
          >
            <span className="flex items-center justify-center gap-3">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              {t('navigation.login')}
            </span>
          </Link>

          {/* Message de contact */}
          <p className="text-sm text-gray-500 mt-6">
            {t('accessDenied.back')}
          </p>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-400">
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </p>
        </div>
      </div>

      {/* Popup d'acceptation des cookies - Affiché systématiquement */}
      <CookieConsent />

      {/* Styles pour l'animation blob */}
      <style>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default AccessDenied;
